/**
 * Portfolio headline total uses the market quote currency (ARS/USD from market
 * context), not the profile display currency. That keeps the summary aligned
 * with the positions list and avoids hiding the currency conversion behind a
 * single ambiguous number.
 */
import React, { useMemo } from "react";
import { Box, IconButton, Paper, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlineRoundedIcon from "@mui/icons-material/InfoOutlineRounded";
import BalanceDisplay from "shared/money/BalanceDisplay";
import { useFinancialVisibility } from "shared/contexts/UserContext";
import { useTranslation } from "shared/hooks/useTranslation";
import type { QuoteCurrency } from "features/market";
import { formatCurrencyAmount, formatCurrencyAmountOrMasked } from "shared/money/balances";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { SCREEN_CARD_WIDTH_SX } from "shared/ui/cardConstants";
import type { Operation, PositionWithAsset } from "types/Portfolio";

export interface InvestmentsBalanceCardProps {
    totalValue: number;
    displayCurrency: QuoteCurrency;
    loading?: boolean;
    positions: PositionWithAsset[];
    operations: Operation[];
    density?: "page" | "panel";
}

function investedValue(position: PositionWithAsset): number {
    return position.quantity * position.avgPrice;
}

function signedAmountLabel(amount: number, currency: string, visible: boolean): string {
    const label = formatCurrencyAmountOrMasked(Math.abs(amount), currency, visible);
    if (!visible || Math.abs(amount) < 0.01) return label;
    return `${amount >= 0 ? "+" : "-"}${label}`;
}

const InvestmentsBalanceCard: React.FC<InvestmentsBalanceCardProps> = ({
    totalValue,
    displayCurrency,
    loading = false,
    positions,
    operations,
    density = "page",
}) => {
    const { t } = useTranslation();
    const { convertAmount } = useCurrencyConversion();
    const isPanel = density === "panel";
    const { balancesVisible } = useFinancialVisibility();

    const invested = useMemo(
        () => positions.reduce((sum, position) => sum + convertAmount(
            investedValue(position),
            position.currency || displayCurrency,
            { toCurrency: displayCurrency },
        ), 0),
        [convertAmount, displayCurrency, positions],
    );
    const conversionEquations = useMemo(() => Array.from(new Set(positions
        .map((position) => position.currency || displayCurrency)
        .filter((currency) => currency !== displayCurrency)))
        .flatMap((currency) => {
            const converted = convertAmount(1, currency, { toCurrency: displayCurrency });
            if (!Number.isFinite(converted)) return [];
            return [`1 ${currency} = ${formatCurrencyAmount(converted, displayCurrency)}`];
        }), [convertAmount, displayCurrency, positions]);
    const result = totalValue - invested;
    const resultPct = invested > 0 ? (result / invested) * 100 : null;
    const lastOperationDate = useMemo(() => {
        let latest = 0;
        for (const operation of operations) {
            const timestamp = operation.date ? new Date(operation.date).getTime() : 0;
            if (Number.isFinite(timestamp)) latest = Math.max(latest, timestamp);
        }
        return latest ? new Date(latest).toLocaleDateString() : undefined;
    }, [operations]);

    return (
        <Paper
            elevation={0}
            sx={{
                ...SCREEN_CARD_WIDTH_SX,
                overflow: "hidden",
                p: isPanel ? 1.75 : { xs: 2.25, sm: 3 },
                borderRadius: isPanel ? 3 : 4,
                bgcolor: "background.paper",
                backgroundImage: (theme) => theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.22)}, ${alpha(theme.palette.background.paper, 0.94)} 62%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.background.paper, 0.99)} 56%)`,
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.16)}`,
                boxShadow: (theme) => theme.palette.mode === "dark"
                    ? `0 18px 44px ${alpha(theme.palette.common.black, 0.28)}`
                    : `0 18px 40px ${alpha(theme.palette.common.black, 0.08)}`,
            }}
        >
            <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 850 }}>
                            {t("portfolio.currentValue")}
                        </Typography>
                        <Stack direction="row" spacing={0.25} alignItems="center">
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                {t("portfolio.viewingIn", { currency: displayCurrency })}
                            </Typography>
                            <Tooltip title={(
                                <Box>
                                    <Typography variant="body2">{t("portfolio.conversionHelp", { currency: displayCurrency })}</Typography>
                                    {conversionEquations.map((equation) => <Typography key={equation} variant="caption" component="div">{equation}</Typography>)}
                                </Box>
                            )}>
                                <IconButton size="small" aria-label={t("portfolio.conversionHelp", { currency: displayCurrency })} sx={{ p: 0.25, color: "text.secondary" }}>
                                    <InfoOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                    {lastOperationDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 750, textAlign: "right" }}>
                            {t("portfolio.latestOperation", { date: lastOperationDate })}
                        </Typography>
                    )}
                </Box>

                {loading ? (
                    <Box>
                        <Skeleton width="76%" height={58} />
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 2 }}>
                            <Skeleton height={48} />
                            <Skeleton height={48} />
                        </Box>
                    </Box>
                ) : (
                    <>
                        <BalanceDisplay
                            amount={totalValue}
                            currency={displayCurrency}
                            keepOriginalCurrency
                            isPositive={totalValue >= 0}
                            variant={isPanel ? "h4" : "h3"}
                            fontSize={isPanel ? "1.75rem" : "2.5rem"}
                            showCurrencyCode
                        />

                        {isPanel ? (
                            <Typography
                                sx={{
                                    color: resultPct == null ? "text.secondary" : result >= 0 ? "success.main" : "error.main",
                                    fontWeight: 900,
                                    fontSize: 16,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {resultPct == null || !balancesVisible
                                    ? t("portfolio.noResultYet")
                                    : `${resultPct >= 0 ? "+" : ""}${resultPct.toFixed(2)}%`}
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                                    gap: 1.25,
                                    pt: 1.5,
                                    borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.08)}`,
                                }}
                            >
                                <SummaryMetric
                                    label={t("portfolio.invested")}
                                    value={invested}
                                    currency={displayCurrency}
                                    balancesVisible={balancesVisible}
                                />
                                <SummaryMetric
                                    label={t("portfolio.estimatedResult")}
                                    value={result}
                                    currency={displayCurrency}
                                    semantic
                                    balancesVisible={balancesVisible}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800 }}>
                                        {t("portfolio.result")}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 0.35,
                                            color: resultPct == null ? "text.secondary" : result >= 0 ? "success.main" : "error.main",
                                            fontWeight: 900,
                                            fontSize: 20,
                                            lineHeight: 1.1,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {resultPct == null || !balancesVisible
                                            ? t("portfolio.noResultYet")
                                            : `${resultPct >= 0 ? "+" : ""}${resultPct.toFixed(2)}%`}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Stack>
        </Paper>
    );
};

function SummaryMetric({
    label,
    value,
    currency,
    balancesVisible,
    semantic = false,
}: {
    label: string;
    value: number;
    currency: string;
    balancesVisible: boolean;
    semantic?: boolean;
}) {
    const color = semantic
        ? value >= 0 ? "success.main" : "error.main"
        : "text.primary";

    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800 }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    mt: 0.35,
                    color,
                    fontWeight: 900,
                    fontSize: 20,
                    lineHeight: 1.1,
                    fontVariantNumeric: "tabular-nums",
                    overflowWrap: "anywhere",
                }}
            >
                {semantic
                    ? signedAmountLabel(value, currency, balancesVisible)
                    : formatCurrencyAmountOrMasked(value, currency, balancesVisible)}
            </Typography>
        </Box>
    );
}

export default InvestmentsBalanceCard;
